import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { ConfigService } from '@mona/config';
import { Logger } from '@mona/shared/logger';

/**
 * Allowed event types — must mirror the FE_EVENT_TYPES allowlist on
 * the BE (``mona/projects/api/diagnostics_views.py``). Drift here
 * means the BE returns 400 and the batch is dropped, so the lists
 * are intentionally tiny and stable.
 */
export type FeEventType = 'nav' | 'error' | 'unhandled_rejection' | 'http_failure' | 'app_lifecycle';

/**
 * On-the-wire shape of one FE event. All ids are uuid4.
 *
 * ``request_id`` carries the X-Request-Id of the *triggering* call
 * (e.g. the failing GET that produced an http_failure event), NOT
 * the id of the upload itself. That's what links each event back
 * to a specific BE log line.
 */
export interface FeEvent {
    type: FeEventType;
    event_id: string;
    session_id: string;
    emitted_at: string;
    request_id?: string;
    route?: string;
    payload?: Record<string, unknown>;
}

/**
 * Optional DI override for FeEventsService configuration. The default
 * configuration is fine for production; tests provide tighter timeouts
 * and a smaller buffer so they finish quickly.
 */
export interface FeEventsConfig {
    /** Periodic flush cadence. Default 30 000 ms. */
    flushIntervalMs?: number;
    /** Hard cap on the in-memory buffer. Default 500. */
    bufferCap?: number;
    /** Max events POSTed in one request. Must be ≤ BE MAX_EVENTS_PER_BATCH=100. Default 100. */
    batchSize?: number;
    /** Endpoint path appended to api.baseUrl. Default ``/api/diag/fe-events/``. */
    endpointPath?: string;
    /** Master kill-switch. Default true. */
    enabled?: boolean;
    /** Cap on exponential backoff. Default 5 minutes. */
    maxBackoffMs?: number;
}

export const FE_EVENTS_CONFIG = new InjectionToken<FeEventsConfig>('FeEventsConfig');

/**
 * Per-terminal buffer + uploader for FE observability events.
 *
 * Phase 3 of OBSERVABILITY_FE_BE_CORRELATION.md. Sits behind a 30 s
 * tick and POSTs accumulated events to ``/api/diag/fe-events/`` on the
 * mona BE, which logs each one as a single ``logger.info`` line so
 * Vector ships them to VictoriaLogs alongside ordinary BE log records.
 *
 * Lifecycle
 * =========
 * The service is ``providedIn: 'root'`` so a single instance lives
 * for the lifetime of the renderer process, mints a stable
 * ``session_id`` on construction, and starts the flush loop only if
 * the master flag is on. ``stop()`` is called by ``app.before-quit``
 * (or the equivalent FE shutdown hook) to clear the timer.
 *
 * Failure modes
 * =============
 * **4xx (except 429)** — the batch is dropped (server rejects
 *   permanently; retrying would loop forever). One ``logger.warn``
 *   per drop so the operator notices a serializer drift.
 * **5xx / 429 / network** — batch is re-queued at the **front** of
 *   the buffer and an exponential backoff is applied to the next
 *   tick. If the buffer overflows the cap during the outage, the
 *   oldest events are evicted (not the failed retry — that wins
 *   because the operator usually cares about the most recent crash
 *   more than first-boot navigation).
 *
 * Why we don't use rxjs ``timer`` / ``scheduled``
 * -----------------------------------------------
 * Angular's ``zone.js`` patches ``setTimeout`` already; using the
 * raw scheduler keeps the service trivially testable with
 * ``jest.useFakeTimers()`` and avoids spinning up an RxJS pipeline
 * for what is fundamentally a single recurring callback.
 */
@Injectable({ providedIn: 'root' })
export class FeEventsService {
    private readonly logger = new Logger('FE-EVENTS');
    private readonly cfg: Required<FeEventsConfig>;
    private readonly buffer: FeEvent[] = [];
    private readonly _sessionId: string;
    private flushTimer: ReturnType<typeof setTimeout> | undefined;
    private currentBackoffMs = 0;
    private inFlight = false;
    private destroyed = false;

    constructor(
        private readonly http: HttpClient,
        @Optional() private readonly configService: ConfigService | null,
        @Optional() @Inject(FE_EVENTS_CONFIG) cfg: FeEventsConfig | null,
    ) {
        this.cfg = {
            flushIntervalMs: 30_000,
            bufferCap: 500,
            batchSize: 100,
            endpointPath: '/api/diag/fe-events/',
            enabled: true,
            maxBackoffMs: 5 * 60_000,
            ...(cfg ?? {}),
        };
        this._sessionId = FeEventsService.uuid();

        if (this.cfg.enabled) {
            this.scheduleFlush(this.cfg.flushIntervalMs);
        }
    }

    /** Stable per-renderer-process id. Logged with every event. */
    get sessionId(): string {
        return this._sessionId;
    }

    /** Approximate buffer occupancy — used by tests + diagnostics UI. */
    get bufferSize(): number {
        return this.buffer.length;
    }

    /**
     * Buffer one event. Synchronous, never throws — call sites are on
     * hot error paths and an exception here would mask the real bug.
     * @param type        Allowlisted event kind.
     * @param payload     Optional structured fields. The BE recursively
     *                    scrubs PHI / PII shapes before logging. Keep
     *                    payload ≤ ~4 KB; oversize batches are rejected
     *                    server-side.
     * @param originRequestId  X-Request-Id of the triggering HTTP call,
     *                    if any. Caller is responsible for plumbing
     *                    this through (HttpErrorInterceptor extracts
     *                    it from ``request.headers``).
     */
    emit(type: FeEventType, payload?: Record<string, unknown>, originRequestId?: string): void {
        if (!this.cfg.enabled || this.destroyed) {
            return;
        }
        try {
            const ev: FeEvent = {
                type,
                event_id: FeEventsService.uuid(),
                session_id: this._sessionId,
                emitted_at: new Date().toISOString(),
                request_id: originRequestId,
                route: this.currentRoute(),
                payload,
            };
            // Ring eviction: drop oldest when full so a retry storm
            // doesn't OOM the renderer.
            if (this.buffer.length >= this.cfg.bufferCap) {
                this.buffer.shift();
            }
            this.buffer.push(ev);
        } catch (err) {
            // Last-resort guard. ``emit`` is the surface every error
            // path calls into; a throw here would cascade.
            this.logger.warn('failed to buffer FE event', err);
        }
    }

    /**
     * Force-flush the buffer immediately. Called by the FE shutdown
     * hook and by tests. Resolves regardless of success — the caller
     * doesn't get to know whether the batch landed because there's
     * nothing useful they could do with the answer.
     */
    flush(): Promise<void> {
        if (this.inFlight || this.buffer.length === 0 || !this.cfg.enabled) {
            return Promise.resolve();
        }
        const baseUrl = this.resolveBaseUrl();
        if (!baseUrl) {
            // Config not loaded yet — try on the next tick.
            return Promise.resolve();
        }

        const batch = this.buffer.splice(0, this.cfg.batchSize);
        this.inFlight = true;

        return new Promise<void>(resolve => {
            this.http.post(`${baseUrl}${this.cfg.endpointPath}`, { events: batch }).subscribe({
                next: () => {
                    this.inFlight = false;
                    this.currentBackoffMs = 0;
                    resolve();
                },
                error: err => {
                    this.inFlight = false;
                    if (FeEventsService.isPermanentClientError(err)) {
                        this.logger.warn(
                            `dropping ${batch.length} FE event(s); server returned ${
                                (err as HttpErrorResponse).status
                            } (permanent)`,
                        );
                        resolve();
                        return;
                    }
                    // Re-queue at the FRONT so subsequent emits don't
                    // jump ahead of the retry. Truncate to cap if the
                    // buffer overflowed during the request.
                    this.buffer.unshift(...batch);
                    if (this.buffer.length > this.cfg.bufferCap) {
                        this.buffer.length = this.cfg.bufferCap;
                    }
                    this.bumpBackoff();
                    resolve();
                },
            });
        });
    }

    /**
     * Stop the periodic flush. Idempotent.
     *
     * The buffer itself is preserved so a final ``flush()`` after
     * ``stop()`` (e.g. on app.before-quit) can still drain it.
     */
    stop(): void {
        this.destroyed = true;
        if (this.flushTimer !== undefined) {
            clearTimeout(this.flushTimer);
            this.flushTimer = undefined;
        }
    }

    private scheduleFlush(delayMs: number): void {
        if (this.destroyed) {
            return;
        }
        if (this.flushTimer !== undefined) {
            clearTimeout(this.flushTimer);
        }
        this.flushTimer = setTimeout(() => {
            this.flushTimer = undefined;
            const onSettled = () => {
                this.scheduleFlush(this.cfg.flushIntervalMs + this.currentBackoffMs);
            };
            this.flush().then(onSettled, onSettled);
        }, delayMs);
    }

    private bumpBackoff(): void {
        if (this.currentBackoffMs === 0) {
            this.currentBackoffMs = 5_000;
        } else {
            this.currentBackoffMs = Math.min(this.currentBackoffMs * 2, this.cfg.maxBackoffMs);
        }
    }

    private resolveBaseUrl(): string | undefined {
        if (!this.configService) {
            return undefined;
        }
        try {
            const api = this.configService.get('api' as never) as { baseUrl?: string } | undefined;
            return api?.baseUrl;
        } catch {
            return undefined;
        }
    }

    private currentRoute(): string | undefined {
        try {
            if (typeof window === 'undefined' || !window.location) {
                return undefined;
            }
            // Hash router + path-style both supported. We log neither
            // search nor full hash query strings — those frequently
            // contain free-text typed by the user.
            const hash = window.location.hash || '';
            const cleanHash = hash.split('?')[0];
            return cleanHash || window.location.pathname || undefined;
        } catch {
            return undefined;
        }
    }

    private static isPermanentClientError(err: unknown): boolean {
        if (!(err instanceof HttpErrorResponse)) {
            return false;
        }
        // 429 = rate limited (retry with backoff); other 4xx = the
        // server permanently rejects this batch shape, so no point
        // retrying.
        return err.status >= 400 && err.status < 500 && err.status !== 429;
    }

    private static uuid(): string {
        const cryptoApi: Crypto | undefined =
            typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined;
        if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
            return cryptoApi.randomUUID();
        }
        const bytes = new Array<number>(16);
        for (let i = 0; i < 16; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
}
